import clsx from "clsx";
import { type ComponentProps, useCallback, useMemo } from "react";

import { BrandIcon } from "@shared/ui/brand-icon";

import type { Chat, SubquestionsType } from "@/_pages/ai-assistant/model";
import type { ProgramQuestionsType } from "@/_pages/ai-assistant/model/types";
import { LAST_STEP_ID } from "@/_pages/ai-assistant/ui/lib/individual-program";

import { LastStep } from "./steps/lastStep";
import { Step } from "./steps/step";

import s from "./subquestion.module.scss";

export type CompProps = ComponentProps<"div"> & {
  className?: string;
  data: Chat;
  individualStep: string | null;
  isActiveSession: boolean;
  programQuestions: ProgramQuestionsType[];
  subquestionData: SubquestionsType[];
  setSubquestionAnswer: (step: string, answer: string | string[]) => void;
};

export const Subquestion = (props: CompProps) => {
  const {
    className,
    data,
    individualStep,
    isActiveSession,
    programQuestions,
    subquestionData,
    setSubquestionAnswer,
  } = props;
  const isStepDisabled =
    !isActiveSession ||
    individualStep === null ||
    data.subquestionStep !== individualStep;

  const programQuestion = useMemo(() => {
    if (!data.subquestionStep) {
      return null;
    }

    return (
      programQuestions.find(
        (question) => question.documentId === data.subquestionStep,
      ) ?? null
    );
  }, [data.subquestionStep, programQuestions]);

  const currentStepData = useMemo(() => {
    if (!data.subquestionStep) {
      return undefined;
    }

    return subquestionData.find((item) => item.stepId === data.subquestionStep);
  }, [data.subquestionStep, subquestionData]);

  const getContent = useCallback(() => {
    if (!data.subquestionStep) {
      return null;
    }

    if (data.subquestionStep === LAST_STEP_ID) {
      return (
        <LastStep
          question={currentStepData?.question ?? ""}
          step={data.subquestionStep}
          answer={currentStepData?.answer}
          disabled={isStepDisabled}
          onChangeAnswer={setSubquestionAnswer}
        />
      );
    }

    if (!programQuestion) {
      return null;
    }

    return (
      <Step
        data={programQuestion}
        step={data.subquestionStep}
        answer={currentStepData?.answer}
        disabled={isStepDisabled}
        onChangeAnswer={setSubquestionAnswer}
      />
    );
  }, [
    currentStepData,
    data.subquestionStep,
    isStepDisabled,
    programQuestion,
    setSubquestionAnswer,
  ]);

  return (
    <div className={clsx(s.root, className)}>
      <BrandIcon
        name="three-circle"
        fillColor="violet"
        strokeColor="milk"
        className={s.anserIcon}
      />
      {getContent()}
    </div>
  );
};

Subquestion.displayName = "Subquestion";
